import { useEffect, useRef, useState } from 'react'
import { api, type AttachmentSource } from './api.ts'

/**
 * The prescription's pages, as blob URLs.
 *
 * Fetched rather than linked: the image routes are behind the session
 * middleware, an <img> cannot carry an Authorization header, and a token in the
 * URL would write a credential into every server log.
 *
 * Shared by both review modes. It lived inline in the full detail view, which
 * is why the guided pass -- the mode that most needs the image, since the whole
 * point is that the highlight follows the step -- was rendering the viewer with
 * no pages at all.
 */
export interface PrescriptionPage {
  readonly url: string
  /**
   * The number a block cites, which is the attachment's index on the
   * quotation -- not this page's position in the list.
   *
   * They stopped being the same thing when receipts started being left out:
   * a receita that is the second attachment is the *first* thing shown, and a
   * highlight matched on list position would be drawn on the wrong document.
   */
  readonly page: number
  /** A receita a person attached on the conferência is drawn as such (#276). */
  readonly source: AttachmentSource
  /** The attachment this page belongs to (`manual:<id>` for one attached here). */
  readonly attachmentId: string
}

/**
 * Whether the pages are on their way, here, or could not be got.
 *
 * "No image" and "the image did not arrive" used to be the same empty list,
 * and the screen said the first when the second was true: the listing call
 * was cut off by the ingress under load, the hook swallowed that, and a
 * pharmacist read that the receita had no image at all. `unavailable` is
 * kept apart so the screen can say what actually happened -- and it keeps
 * retrying underneath, because the usual cause is transient.
 */
export type PagesStatus = 'loading' | 'ready' | 'unavailable'

/** Seconds between attempts: quick at first, then no faster than this. */
const RETRY_SECONDS = [2, 5, 10, 20]
/** How often to look again while the attachments are still being identified. */
const PENDING_SECONDS = 5
/** After this many misses the screen is told, though the trying goes on. */
const ATTEMPTS_BEFORE_TELLING = 2

/**
 * @param analysing whether a run is happening on this verification right now. The
 * hook looks again when that turns false and it still has nothing to show.
 *
 * It used to settle on the first definitive answer and never ask again. A
 * pharmacist who pressed "tentar de novo" on an verification that had no images --
 * the classifier had failed overnight -- got one listing, taken before the
 * classifier had written anything, and the page then held that answer while
 * the run wrote the images it was waiting for. "I have to refresh the page"
 * was this. The polling covers the case where the server says *pending*; this
 * covers the case where it said something final that a run then made stale.
 */
export function usePrescriptionPages(
  verificationId: string,
  analysing = false,
  /**
   * Bumped when the documents changed under the screen -- a receita attached
   * or removed (#276). The pages already shown are then stale, and are listed
   * again once the run that reads the new set is over.
   */
  revision = 0,
): {
  readonly pages: readonly PrescriptionPage[]
  readonly status: PagesStatus
} {
  const [pages, setPages] = useState<readonly PrescriptionPage[]>([])
  const [status, setStatus] = useState<PagesStatus>('loading')
  /** Bumped to start the fetching over; the effect below keys on it. */
  const [round, setRound] = useState(0)
  const shown = useRef(0)
  shown.current = pages.length

  const wasAnalysing = useRef(analysing)
  const stale = useRef(false)
  useEffect(() => {
    if (revision > 0) stale.current = true
  }, [revision])
  useEffect(() => {
    const ended = wasAnalysing.current && !analysing
    wasAnalysing.current = analysing
    // Only with nothing on screen, or with the documents known to have
    // changed: otherwise a page that already has its images keeps them, and
    // re-listing would blank it to fetch the same files again.
    if (ended && (shown.current === 0 || stale.current)) {
      stale.current = false
      setRound((n) => n + 1)
    }
  }, [analysing])

  useEffect(() => {
    let live = true
    let timer: ReturnType<typeof setTimeout> | undefined
    const created: PrescriptionPage[] = []
    setStatus('loading')

    async function attempt(failures: number): Promise<void> {
      const answer = await api.pages(verificationId)
      if (!live) return

      // Not yet: the verification is still identifying its attachments. Not a
      // failure, so it does not count as one -- just look again shortly.
      if (answer?.pending === true) {
        setStatus('loading')
        timer = setTimeout(() => void attempt(0), PENDING_SECONDS * 1000)
        return
      }

      const refs = answer?.pages
      if (refs !== undefined) {
        for (const ref of refs) {
          const url = await api.pageBlob(verificationId, ref.id, ref.pageIndex)
          if (!live) return
          if (url !== undefined)
            created.push({ url, page: ref.page, source: ref.source, attachmentId: ref.id })
        }
        // A listing with pages none of which arrived is the same failure
        // one call later, not a receita without an image.
        if (refs.length === 0 || created.length > 0) {
          setPages([...created])
          setStatus('ready')
          return
        }
      }

      if (failures + 1 >= ATTEMPTS_BEFORE_TELLING) setStatus('unavailable')
      const wait = RETRY_SECONDS[Math.min(failures, RETRY_SECONDS.length - 1)]!
      timer = setTimeout(() => void attempt(failures + 1), wait * 1000)
    }

    void attempt(0)

    return () => {
      live = false
      if (timer !== undefined) clearTimeout(timer)
      // Revoked on unmount: a blob URL holds the image in memory until it is,
      // and a pharmacist working a queue opens a great many of them.
      for (const one of created) URL.revokeObjectURL(one.url)
      setPages([])
    }
  }, [verificationId, round])

  return { pages, status }
}
